import api from "../../../services/api";
import {
  AmortizationSystem,
  IFinancingInsight,
  IKenloPaymentsData,
  IMarketIndices,
  IOwnerData,
  IOwnerDividend,
  IOwnerKenloEndpoints,
  IOwnerOffersData,
  IPaginatedFinancingInsights,
  IPaginatedPropertyReturns,
  IPropertyReturn,
  IPropertyAnalyticsData,
  IPendingSignaturesData,
  AvailableOwnerListing,
  IVisitPeriod,
  YieldBasis,
} from "../types/ownerDashboard.types";
import { KenloPaymentsQuery, buildKenloPaymentsParams } from "../utils/buildKenloPaymentsParams";
import {
  PropertyAnalyticsQuery,
  buildPropertyAnalyticsParams,
} from "../utils/buildPropertyAnalyticsParams";
import { OwnerOffersQuery, buildOwnerOffersParams } from "../utils/buildOwnerOffersParams";
import {
  PendingSignaturesQuery,
  buildPendingSignaturesParams,
} from "../utils/buildPendingSignaturesParams";
import { selectAvailableOwnerListings } from "../utils/selectAvailableOwnerListings";
import { normalizeOwnerOffersData } from "../utils/normalizeOwnerOffersData";
import { normalizeRentedPropertiesDetails } from "../utils/normalizeRentedPropertiesDetails";
import { normalizeFinancingInsight } from "../utils/normalizeFinancingInsight";
import { normalizePropertyReturn } from "../utils/normalizePropertyReturn";
import { normalizePropertyAnalyticsData } from "../utils/normalizePropertyAnalyticsData";
import { normalizePendingSignatures } from "../utils/normalizePendingSignatures";
import { normalizeKenloPaymentsData } from "../utils/normalizeKenloPaymentsData";
import { normalizeKenloPropertyId } from "../utils/buildPropertyLabelMap";
import { resolveSignerKeyEndpoint } from "../utils/resolveSignerKeyEndpoint";
import { resolveDownloadEndpoint } from "../utils/resolveDownloadEndpoint";
import { resolveEnvelopeEndpoint } from "../utils/resolveEnvelopeEndpoint";
import { fetchAllPaginatedItems } from "../utils/fetchAllPaginatedItems";
import { resolveOwnerDashboardEndpoint } from "../utils/resolveOwnerDashboardEndpoint";

export interface DashboardDateRangeParams {
  initial_date?: string;
  final_date?: string;
}

export interface PaginationParams {
  page?: number;
  per_page?: number;
}

export interface PropertyReturnsParams extends PaginationParams {
  yield_basis?: YieldBasis;
}

export interface CreatePropertyValuePayload {
  kenlo_property_id: string;
  value: number;
  market_value?: number | null;
  purchase_date?: string;
}

export interface UpdatePropertyValuePayload {
  value?: number;
  market_value?: number | null;
  purchase_date?: string | null;
}

export interface CreateFinancingPayloadBase {
  kenlo_property_id: string;
  amortization_system: AmortizationSystem;
  payment_start_date: string;
}

export type CreateFinancingPayload =
  | (CreateFinancingPayloadBase & {
      amortization_system: "price";
      monthly_installment: number;
    })
  | (CreateFinancingPayloadBase & {
      amortization_system: "sac";
      financed_amount: number;
      annual_interest_rate_pct: number;
      installment_count: number;
    });

export async function fetchOwnerDashboard(
  params?: DashboardDateRangeParams
): Promise<IOwnerData> {
  const response = await api.get("/api/client/owner/dashboard", { params });
  const data = response.data.data as IOwnerData;

  return {
    ...data,
    additional_data: {
      ...data.additional_data,
      rented_properties_details: normalizeRentedPropertiesDetails(
        data.additional_data?.rented_properties_details
      ),
    },
  };
}

export async function fetchOwnerDividends(): Promise<IOwnerDividend[]> {
  const response = await api.get("/api/client/owner/dividend");
  const payload = response.data?.data ?? response.data;

  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.map((item: IOwnerDividend) => ({
    ...item,
    kenlo_property_id: normalizeKenloPropertyId(item.kenlo_property_id),
    property_label: item.property_label?.trim() || null,
    property_address: item.property_address?.trim() || null,
  }));
}

export async function fetchKenloPayments(
  query: KenloPaymentsQuery,
  endpoints?: IOwnerKenloEndpoints
): Promise<IKenloPaymentsData> {
  const url = resolveOwnerDashboardEndpoint(endpoints, "kenlo_payments");
  const response = await api.get(url, { params: buildKenloPaymentsParams(query) });
  return normalizeKenloPaymentsData(response.data.data);
}

export async function fetchPropertyAnalytics(
  query: PropertyAnalyticsQuery,
  endpoints?: IOwnerKenloEndpoints
): Promise<IPropertyAnalyticsData> {
  const url = resolveOwnerDashboardEndpoint(endpoints, "property_analytics");
  const response = await api.get(url, { params: buildPropertyAnalyticsParams(query) });
  return normalizePropertyAnalyticsData(response.data.data);
}

export async function fetchOwnerOffers(
  query: OwnerOffersQuery,
  endpoints?: IOwnerKenloEndpoints
): Promise<IOwnerOffersData> {
  const url = resolveOwnerDashboardEndpoint(endpoints, "offers");
  const response = await api.get(url, { params: buildOwnerOffersParams(query) });
  return normalizeOwnerOffersData(response.data.data);
}

export async function fetchPendingSignatures(
  query: PendingSignaturesQuery,
  endpoints?: IOwnerKenloEndpoints
): Promise<IPendingSignaturesData> {
  const url = resolveOwnerDashboardEndpoint(endpoints, "pending_signatures");
  const response = await api.get(url, { params: buildPendingSignaturesParams(query) });
  const payload = response.data?.data ?? response.data;

  return normalizePendingSignatures(payload);
}

const OWNER_PROPERTIES_ENDPOINT = "/api/client/owner/properties";

export async function fetchAvailableOwnerListings(): Promise<AvailableOwnerListing[]> {
  const response = await api.get(OWNER_PROPERTIES_ENDPOINT);
  return selectAvailableOwnerListings(response.data);
}

export async function fetchSignerKey(
  rentProcessId: number,
  signerKeyEndpoint?: string
): Promise<string> {
  const url = resolveSignerKeyEndpoint(rentProcessId, signerKeyEndpoint);
  const response = await api.get(url);
  const signerKey = response.data?.data?.signer_key;

  if (typeof signerKey !== "string" || !signerKey.trim()) {
    throw new Error("Signer key not found");
  }

  return signerKey;
}

export async function fetchRentContractDownloadUrl(
  rentProcessId: number,
  downloadEndpoint?: string
): Promise<string> {
  const url = resolveDownloadEndpoint(rentProcessId, downloadEndpoint);
  const response = await api.get(url);
  const downloadUrl = response.data?.data?.url;

  if (typeof downloadUrl !== "string" || !downloadUrl.trim()) {
    throw new Error("Download URL not found");
  }

  return downloadUrl;
}

export async function fetchClickSignEnvelopeDownloadUrl(
  envelopeId: string,
  downloadEndpoint: string
): Promise<string> {
  const url = resolveEnvelopeEndpoint(envelopeId, downloadEndpoint);
  const response = await api.get(url);
  const downloadUrl = response.data?.data?.url ?? response.data?.data?.download_url;

  if (typeof downloadUrl !== "string" || !downloadUrl.trim()) {
    throw new Error("ClickSign download URL not found");
  }

  return downloadUrl;
}

export async function notifyClickSignEnvelope(
  envelopeId: string,
  notifyEndpoint: string
): Promise<void> {
  const url = resolveEnvelopeEndpoint(envelopeId, notifyEndpoint);
  await api.post(url);
}

export async function fetchMarketIndices(
  endpoints?: IOwnerKenloEndpoints
): Promise<IMarketIndices> {
  const url = resolveOwnerDashboardEndpoint(endpoints, "market_indices");
  const response = await api.get(url);
  return response.data.data;
}

export async function fetchPropertyReturns(
  params: PropertyReturnsParams = {},
  endpoints?: IOwnerKenloEndpoints
): Promise<IPaginatedPropertyReturns> {
  const url = resolveOwnerDashboardEndpoint(endpoints, "property_returns");
  const response = await api.get(url, { params });
  const data = response.data.data as IPaginatedPropertyReturns;

  return {
    ...data,
    items: data.items.map(normalizePropertyReturn),
  };
}

export async function fetchFinancingInsights(
  params: PaginationParams = {},
  endpoints?: IOwnerKenloEndpoints
): Promise<IPaginatedFinancingInsights> {
  const url = resolveOwnerDashboardEndpoint(endpoints, "financing_insights");
  const response = await api.get(url, { params });
  const data = response.data.data as IPaginatedFinancingInsights;

  return {
    ...data,
    items: data.items.map(normalizeFinancingInsight),
  };
}

export async function fetchAllPropertyReturns(
  endpoints?: IOwnerKenloEndpoints
): Promise<IPropertyReturn[]> {
  return fetchAllPaginatedItems(async (page, perPage) => {
    const response = await fetchPropertyReturns({ page, per_page: perPage }, endpoints);
    return {
      items: response.items,
      pagination: response.pagination,
    };
  });
}

export async function fetchAllFinancingInsights(
  endpoints?: IOwnerKenloEndpoints
): Promise<IFinancingInsight[]> {
  return fetchAllPaginatedItems(async (page, perPage) => {
    const response = await fetchFinancingInsights({ page, per_page: perPage }, endpoints);
    return {
      items: response.items,
      pagination: response.pagination,
    };
  });
}

export async function createPropertyValue(payload: CreatePropertyValuePayload): Promise<void> {
  await api.post("/api/client/owner/dividend", payload);
}

export async function updatePropertyValue(
  dividendId: number,
  payload: UpdatePropertyValuePayload
): Promise<void> {
  await api.patch(`/api/client/owner/dividend/${dividendId}`, payload);
}

export async function createPropertyFinancing(payload: CreateFinancingPayload): Promise<void> {
  await api.post("/api/client/owner/financing", payload);
}

export async function updatePropertyFinancing(
  financingId: number,
  payload: CreateFinancingPayload
): Promise<void> {
  await api.patch(`/api/client/owner/financing/${financingId}`, payload);
}

export function buildDashboardPeriodFromMonth(date: string): IVisitPeriod {
  const [year, month] = date.split("-");
  const lastDay = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
  const initial_date = `${month}/01/${year}`;
  const final_date = `${month}/${lastDay}/${year}`;

  return { initial_date, final_date };
}
